/**
 * Timezone utilities for the application
 */
import { utcToZonedTime } from 'date-fns-tz';
import { format } from 'date-fns';
import i18n from '../i18n';

/**
 * Get the current locale for date formatting
 */
const getDateLocale = (): string => {
  return i18n.language || 'en';
};

// Array of common timezones grouped by GMT offset
export const TIMEZONES = [
  { value: "Pacific/Honolulu", label: "GMT-10:00 Hawaii" },
  { value: "America/Anchorage", label: "GMT-09:00 Alaska" },
  { value: "America/Los_Angeles", label: "GMT-08:00 Pacific Time" },
  { value: "America/Denver", label: "GMT-07:00 Mountain Time" },
  { value: "America/Chicago", label: "GMT-06:00 Central Time" },
  { value: "America/New_York", label: "GMT-05:00 Eastern Time" },
  { value: "America/Halifax", label: "GMT-04:00 Atlantic Time" },
  { value: "America/Sao_Paulo", label: "GMT-03:00 Brasilia" },
  { value: "UTC", label: "GMT+00:00 UTC" },
  { value: "Europe/London", label: "GMT+00:00 London" },
  { value: "Europe/Berlin", label: "GMT+01:00 Berlin, Paris, Rome" },
  { value: "Europe/Helsinki", label: "GMT+02:00 Helsinki" },
  { value: "Asia/Dubai", label: "GMT+04:00 Dubai" },
  { value: "Asia/Kolkata", label: "GMT+05:30 India" },
  { value: "Asia/Bangkok", label: "GMT+07:00 Bangkok" },
  { value: "Asia/Singapore", label: "GMT+08:00 Singapore" },
  { value: "Asia/Tokyo", label: "GMT+09:00 Tokyo" },
  { value: "Australia/Sydney", label: "GMT+10:00 Sydney" },
  { value: "Pacific/Auckland", label: "GMT+12:00 Auckland" },
];



/**
 * Custom implementation of formatInTimeZone for compatibility with date-fns-tz v1.1.6
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatInTimeZoneCustom = (date: Date, timezone: string, formatString: string): string => {
  const zonedDate = utcToZonedTime(date, timezone);
  return format(zonedDate, formatString);
};

/**
 * Format a date in a specific timezone
 * @param date The date to format
 * @param timezone The timezone to format the date in
 * @param options The formatting options
 * @returns The formatted date string
 */
export const formatDateInTimezone = (
  date: Date,
  timezone: string,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZoneName: "short",
  }
): string => {
  try {
    const locale = getDateLocale();
    const isJapanese = locale === 'ja';

    // Adjust hour12 based on locale if not explicitly set
    const adjustedOptions = { ...options };
    if (adjustedOptions.hour && adjustedOptions.hour12 === undefined) {
      adjustedOptions.hour12 = !isJapanese; // 24-hour for Japanese, 12-hour for English
    }

    // First try using Intl.DateTimeFormat
    return new Date(date).toLocaleString(locale, {
      ...adjustedOptions,
      timeZone: timezone,
    });
  } catch (error) {
    // Build format string from options as fallback
    const formatString = getFormatStringFromOptions(options);

    // Use custom implementation for date-fns-tz v1.1.6
    const zonedDate = utcToZonedTime(date, timezone);
    return format(zonedDate, formatString);
  }
};

/**
 * Convert date-fns format string from Intl.DateTimeFormatOptions
 */
function getFormatStringFromOptions(options: Intl.DateTimeFormatOptions): string {
  let formatString = '';
  
  if (options.weekday) {
    switch (options.weekday) {
      case 'long':
        formatString += 'EEEE, ';
        break;
      case 'short':
        formatString += 'EEE, ';
        break;
      case 'narrow':
        formatString += 'EEEEE, ';
        break;
    }
  }
  
  if (options.month) {
    switch (options.month) {
      case 'long':
        formatString += 'MMMM ';
        break;
      case 'short':
        formatString += 'MMM ';
        break;
      case 'narrow':
        formatString += 'MMMMM ';
        break;
      case 'numeric':
        formatString += 'MM/';
        break;
      case '2-digit':
        formatString += 'MM/';
        break;
    }
  }
  
  if (options.day) {
    switch (options.day) {
      case 'numeric':
        formatString += 'd, ';
        break;
      case '2-digit':
        formatString += 'dd, ';
        break;
    }
  }
  
  if (options.year) {
    switch (options.year) {
      case 'numeric':
        formatString += 'yyyy ';
        break;
      case '2-digit':
        formatString += 'yy ';
        break;
    }
  }
  
  if (options.hour) {
    if (options.hour12) {
      formatString += 'h:';
    } else {
      formatString += 'HH:';
    }
  }
  
  if (options.minute) {
    formatString += 'mm';
  }
  
  if (options.second) {
    formatString += ':ss';
  }
  
  if (options.hour12) {
    formatString += ' a';
  }
  
  if (options.timeZoneName) {
    formatString += ' zzz';
  }
  
  return formatString.trim();
}

/**
 * Convert a date to a specific timezone
 */
export const convertToTimezone = (date: Date, timezone: string): Date => {
  try {
    const result = utcToZonedTime(date, timezone);
    return result;
  } catch (error) {
    return new Date(date);
  }
};

 